import React, { useMemo } from "react";
import Avatar from "@mui/material/Avatar";
import { getNameInitials } from "../constants/globalFunctions";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { AvatarGroup } from "@mui/material";
import { RootState } from "@Reducers/index";
import UserTagDataFetcher from "./UserTagDataFetcher";
import { objectIdString } from "@Helpers/schemas/common.schema";
import { isNonZero, isNonEmpty } from "@Helpers/numberCondition";

export type AvatarSize = "XXS" | "XS" | "LSM" | "SM" | "LG" | "LLG" | "XL" | "GR";

const StyledAvatar = styled(Avatar, { shouldForwardProp: (prop) => prop !== "size" })<{ size?: AvatarSize }>(({
  size,
}) => {
  let fontSize = "1rem";
  if (size === "XXS" || size === "XS") {
    fontSize = "0.5rem";
  }

  return {
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    "&.MuiAvatar-root": {
      fontSize,
    },
  };
});

const getSizeClass = (size: AvatarSize = "SM") => {
  switch (size) {
    case "XXS":
      return {
        width: "1.25rem",
        height: "1.25rem",
      };
    case "XS":
      return {
        width: "1.5rem",
        height: "1.5rem",
      };
    case "LSM":
      return {
        width: "1.875rem",
        height: "1.875rem",
      };
    case "LG":
      return {
        width: "3rem",
        height: "3rem",
      };
    case "LLG":
      return {
        width: "4.6875rem",
        height: "4.6875rem",
      };
    case "XL":
      return {
        width: "6.25rem",
        height: "6.25rem",
      };
    case "GR":
      return {
        width: "2.5rem",
        height: "2.5rem",
      };
    case "SM":
    default:
      return {
        width: "2.25rem",
        height: "2.25rem",
      };
  }
};

export interface AvatarComponentProps {
  readonly isCircle?: boolean;
  readonly isSquare?: boolean;
  readonly size?: AvatarSize;
  readonly bgColor?: string;
  readonly style?: React.CSSProperties;
  readonly id?: string | number;
  readonly fullName?: string;
  readonly maxAvatars?: number;
  readonly groupedAvatars?: React.ReactNode;
  readonly userIdsForGroup?: Array<string | number>;
}

function AvatarComponent({
  isSquare = false,
  size = "SM",
  bgColor = "var(--system-colour-2)",
  style,
  id,
  fullName = "",
  maxAvatars,
  groupedAvatars,
  userIdsForGroup = [],
}: AvatarComponentProps) {
  const variant: "circular" | "square" = isSquare ? "square" : "circular";
  const userTagData = useSelector((state: RootState) => state.userTagData);

  // `id` is this shared avatar's loose `string | number` prop (grid RowIds included), so this is
  // the boundary where the account id enters the type system: validate it instead of branding it,
  // otherwise a RowId that is not an account id would be fetched as one. A RowId simply mounts no
  // fetcher below. Memoised because one grid render walks hundreds of these.
  const accountId = useMemo(() => {
    if (id == null) return null;
    const parsed = objectIdString.safeParse(String(id));
    return parsed.success ? parsed.data : null;
  }, [id]);

  const avatarStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    ...getSizeClass(size),
    ...style,
  };

  // The `Any` suffix is now stale (the cast is gone); the identifier is kept because renaming a
  // local const perturbs the minifier's name assignment.
  const AvatarGroupAny = AvatarGroup;

  if (isNonZero(maxAvatars)) {
    return (
      <>
        {accountId !== null && <UserTagDataFetcher accountId={accountId} />}
        <AvatarGroupAny
          variant="circular"
          max={maxAvatars}
          renderSurplus={(surplus: number) => {
            if (surplus >= 1000) {
              return <span>{`${surplus.toString()[0]}k`}</span>;
            }
            return `+${surplus}`;
          }}
          slotProps={{
            // MUI v9 renamed AvatarGroup's `additionalAvatar` slot to `surplus`.
            surplus: {
              sx: {
                ...avatarStyle,
                fontSize: "0.75rem",
                border: "0.125rem solid #fff",
              },
            },
          }}
        >
          {groupedAvatars}
        </AvatarGroupAny>
      </>
    );
  }

  const userDataForId = id == null ? undefined : userTagData[id];

  // The fetched profile for `id`, or null while it is still loading. Held as a value (rather than a
  // boolean "can show" flag) so the branches below narrow it instead of asserting it back.
  const loadedUserData = userDataForId?.isLoaded ? userDataForId.data : null;

  const canShowUserData = userIdsForGroup.length === 0 && !isNonZero(maxAvatars) && isNonEmpty(id);

  let avatarElement: React.ReactNode;

  if (canShowUserData && loadedUserData) {
    if (loadedUserData.profilePic) {
      avatarElement = (
        <StyledAvatar variant={variant} style={avatarStyle} size={size} src={loadedUserData.profilePic} />
      );
    } else {
      avatarElement = (
        <StyledAvatar variant={variant} style={avatarStyle} size={size}>
          {getNameInitials({
            firstName: loadedUserData.firstName,
            lastName: loadedUserData.lastName,
          })}
        </StyledAvatar>
      );
    }
  } else {
    const firstName = fullName.split(" ")[0] || "";
    const lastName = fullName.split(" ")[1] || "";
    avatarElement = (
      <StyledAvatar variant={variant} style={avatarStyle} size={size}>
        {getNameInitials({
          firstName,
          lastName,
        })}
      </StyledAvatar>
    );
  }

  return (
    <>
      {accountId !== null && <UserTagDataFetcher accountId={accountId} />}
      {avatarElement}
    </>
  );
}

export default AvatarComponent;
